/**
 * An inline citation chip typed by its source (§17): file / email / slack / crm
 * / web / derived, each with its own icon + tone. Clicking opens the generic
 * evidence-peek panel. Unknown source variants degrade to a neutral chip.
 */

import type { SourceRef } from "../../types/citationArtifact";
import { describeSource } from "./sourceKind";
import { useEvidencePeek } from "./EvidencePeekContext";

interface SourceChipProps {
	source: SourceRef;
	/** Override the chip label (e.g. a lineage node's role). */
	label?: string;
	className?: string;
}

export function SourceChip({
	source,
	label,
	className,
}: SourceChipProps) {
	const peek = useEvidencePeek();
	const desc = describeSource(source);
	const Icon = desc.Icon;
	const text = label ?? desc.chipLabel;
	const base = `inline-flex max-w-full items-center gap-1 rounded border px-1.5 py-0.5 text-xs font-medium ${desc.tone} ${className ?? ""}`;

	if (!peek) {
		// No peek surface mounted — render a static, non-interactive chip.
		return (
			<span className={base} title={desc.title}>
				<Icon className="h-3 w-3 flex-shrink-0" aria-hidden />
				<span className="truncate">{text}</span>
			</span>
		);
	}

	return (
		<button
			type="button"
			onClick={() => peek.openSource(source)}
			className={`${base} cursor-pointer transition-colors hover:brightness-95`}
			title={desc.title}
			aria-label={`${desc.system} source: ${desc.title}`}
		>
			<Icon className="h-3 w-3 flex-shrink-0" aria-hidden />
			<span className="truncate">{text}</span>
		</button>
	);
}

export default SourceChip;
